/**
 * Make a remote `sampled: false` actually mean "do not record".
 *
 * ═══ THE DEFECT ═══
 *
 * Every sampling decision that arrives from outside the process reaches Effect
 * as an `ExternalSpan`: the browser's `traceparent` (`@effect/platform`
 * `HttpTraceContext.ts:99-102`) and the RPC request payload
 * (`RpcServer.ts:301-308`) both build one with `Tracer.externalSpan(...)`,
 * which sets `sampled` faithfully and leaves `context` EMPTY.
 *
 * `@effect/opentelemetry` then throws that decision away. Its `makeSpanContext`
 * (`dist/esm/internal/tracer.js:287-294`) derives the OTEL trace flags from an
 * `OtelTraceFlags` TAG — first on the new span's context, then on the parent's
 * — and when neither carries it, falls back to:
 *
 *     Option.getOrElse(…, () => OtelApi.TraceFlags.SAMPLED)
 *
 * `ExternalSpan.sampled` is never read. So a parent that said "not sampled"
 * produces an OTEL parent context flagged SAMPLED, `ParentBasedSampler` sees a
 * sampled parent, and the whole server subtree is recorded. Head sampling on
 * the backend cannot work while this holds: the browser's 10% decision is
 * silently upgraded to 100% at the OTEL boundary.
 *
 * ═══ THE FIX ═══
 *
 * A tracer decorator. It delegates everything to the real tracer, and on the
 * way through rewrites one thing: an `ExternalSpan` parent whose context does
 * NOT already carry `OtelTraceFlags` gets the tag added, derived from its own
 * `sampled` field. `makeSpanContext` then reads a real decision instead of
 * defaulting.
 *
 * A parent that already carries the tag is passed through UNTOUCHED — whoever
 * set it (`Tracer.makeExternalSpan({ traceFlags })`) was more specific than we
 * are, and overriding them would be the same class of bug in reverse.
 *
 * Non-external parents (a live `Span` in this process) are untouched too:
 * `populateContext` uses the real OTEL span object for those, so the flags are
 * already correct.
 *
 * ═══ THE SAME DEFECT, ONE STEP UP: NO EFFECT PARENT AT ALL ═══
 *
 * `OtelSpan`'s constructor (`internal/tracer.js:52`) falls back to
 * `getOtelParent(trace, context.active(), …)` whenever the EFFECT parent is
 * `None` but an OTEL span is active on the async context. That fallback builds
 * its own `ExternalSpan` — with an EMPTY context — so `makeSpanContext`
 * defaults it to SAMPLED again, and the decorator never sees it because the
 * decorator only rewrites the `parent` ARGUMENT.
 *
 * An Effect fiber loses its `ParentSpan` more often than it looks:
 * `Effect.cachedWithTTL` / `cachedInvalidateWithTTL` capture the context at
 * CONSTRUCTION time and `provideContext` it over every recompute
 * (`effect/internal/effect/circular.js:172`), so a TTL cache built at
 * layer-build time recomputes with no parent while still running inside the
 * request's OTEL context. That is how the session kill-set read
 * (`SessionKillSetService` → `ActiveSessionRepository.listRevokedSessionIds`)
 * was recorded under a `traceparent` that said `00`.
 *
 * So when the parent is `None` we resolve the active OTEL span OURSELVES and
 * hand it over with its real `traceFlags` (and `traceState`) attached. Same
 * parent the fallback would have picked, minus the invented sampling decision.
 * A span explicitly asking to be a `root` is left parentless, as it asked.
 */
import { context as otelContext, trace as otelTrace, TraceFlags } from "@opentelemetry/api"
import * as OtelTracer from "@effect/opentelemetry/Tracer"
import { Context, Option, Tracer } from "effect"

/**
 * The OTEL span active on the async context, as an `ExternalSpan` that carries
 * its real trace flags. `None` when nothing is active.
 */
const activeOtelParent = (): Option.Option<Tracer.AnySpan> => {
  const active = otelTrace.getSpan(otelContext.active())
  if (active === undefined) return Option.none()
  const spanContext = active.spanContext()
  return Option.some(
    OtelTracer.makeExternalSpan({
      traceId: spanContext.traceId,
      spanId: spanContext.spanId,
      traceFlags: spanContext.traceFlags,
      traceState: spanContext.traceState
    })
  )
}

/**
 * Add `OtelTraceFlags` to an `ExternalSpan` parent that lacks it, deriving the
 * flags from `sampled`; supply the active OTEL span, flags included, when there
 * is no Effect parent at all. Everything else is returned as-is.
 */
const withTraceFlags = (
  parent: Option.Option<Tracer.AnySpan>,
  options?: { readonly root?: boolean }
): Option.Option<Tracer.AnySpan> => {
  if (Option.isNone(parent)) {
    return options?.root === true ? parent : activeOtelParent()
  }
  const span = parent.value
  if (span._tag !== "ExternalSpan") return parent
  if (Option.isSome(Context.getOption(span.context, OtelTracer.OtelTraceFlags))) return parent
  return Option.some({
    ...span,
    context: Context.add(
      span.context,
      OtelTracer.OtelTraceFlags,
      span.sampled ? TraceFlags.SAMPLED : TraceFlags.NONE
    )
  })
}

/**
 * Wrap an Effect `Tracer` so that remote sampling decisions survive the OTEL
 * boundary. Apply it wherever an Effect tracer is installed and requests can
 * arrive with a `traceparent`.
 */
export const honourExternalSampling = (inner: Tracer.Tracer): Tracer.Tracer =>
  Tracer.make({
    span: (name, parent, context, links, startTime, kind, options) =>
      inner.span(name, withTraceFlags(parent, options), context, links, startTime, kind, options),
    context: (execution, fiber) => inner.context(execution, fiber)
  })
